import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import axios from 'axios';
import SearchBar from '../components/SearchBar';
import FilterDrawer from '../components/FilterDrawer';
import ManaCost from '../components/ManaCost';
import { useManaSymbols } from '../hooks/useManaSymbols';
import { Card, Filters, SetOption, GridSize } from './CardBrowser';
import DeckImport from '../components/DeckImport';
import './DeckBuilderPage.css';
import ContextMenu, { ContextMenuItem } from '../components/ContextMenu';
import AddToWishListModal from '../components/AddToWishListModal';

const API = `${process.env.REACT_APP_API_URL || 'https://api.guardianbazaar.com'}/api`;

const DEFAULT_FILTERS: Filters = {
  colors: [], rarities: [], typeLine: '',
  setCodes: [], format: '', keywords: [], tags: [],
  produces: [], producesColorless: false,
  colorless: false,
  ownedOnly: true, showTokens: false,
  showDigital: false, showUnsets: false,
  showArtSeries: false, showNoiseSetTypes: false,
  minPrice: 0, maxPrice: 0, sortBy: 'name',
  cmcMin: 0, cmcMinOp: 'gte', cmcMax: 0, cmcMaxOp: 'lte',
  pipsW: 0, pipsU: 0, pipsB: 0, pipsR: 0, pipsG: 0, pipsC: 0,
  pipsGeneric: 0, pipsX: '',
  pipMode: 'all', pipCount: 'atleast',
  releaseYearFrom: 0, releaseYearTo: 0,
};

const GRID_SIZES: GridSize[] = ['sm', 'md', 'lg', 'xl'];
const GRID_WIDTHS: Record<GridSize, number> = { sm: 110, md: 150, lg: 185, xl: 240 };

// Deck tiles render one size larger than selected — consistent with collection sizes
const DECK_TILE_SIZE: Record<GridSize, number> = {
  sm: GRID_WIDTHS['md'],   // 150px
  md: GRID_WIDTHS['lg'],   // 185px
  lg: GRID_WIDTHS['xl'],   // 240px
  xl: GRID_WIDTHS['xl'],   // unused in deck panel
};
const TYPE_ORDER = ['Commander','Creature','Planeswalker','Battle','Instant','Sorcery','Enchantment','Artifact','Tribal','Land','Other'];
const RARITY_COLOR: Record<string, string> = {
  mythic: '#e8954a', rare: '#c8a45a', uncommon: '#8ab4c8', common: '#9a9aaa',
};

const CATEGORIES = [
  { key: 'total',        label: 'Total'       },
  { key: 'lands',        label: 'Lands'       },
  { key: 'creatures',    label: 'Creatures'   },
  { key: 'ramp',         label: 'Ramp'        },
  { key: 'card_draw',    label: 'Card Draw'   },
  { key: 'removal',      label: 'Removal'     },
  { key: 'counterspell', label: 'Counters'    },
  { key: 'board_wipe',   label: 'Board Wipes' },
];

function TargetBar({
  label, current, target, onEditTarget,
}: {
  label:        string;
  current:      number;
  target:       number;
  onEditTarget: () => void;
}) {
  if (target === 0) return null;
  const pct    = Math.min(Math.round((current / target) * 100), 100);
  const diff   = target - current;
  const color  = diff <= 0 ? '#27ae60' : diff <= 2 ? '#e67e22' : '#e74c3c';
  return (
    <div className="target-bar-row">
      <div className="tbr-label">{label}</div>
      <div className="tbr-track">
        <div className="tbr-fill" style={{ width: `${pct}%`, background: color }} />
      </div>
      <div className="tbr-count" style={{ color }}>
        {current}
      </div>
      <div className="tbr-target" onClick={onEditTarget} title="Click to edit target">
        / {target}
      </div>
    </div>
  );
}

const TAG_CONFIG: { tag: string; label: string; color: string }[] = [
  { tag: 'ramp',        label: '⬆ Ramp',          color: '#27ae60' },
  { tag: 'card_draw',   label: '🃏 Card Draw',      color: '#2980b9' },
  { tag: 'removal',     label: '💥 Removal',        color: '#c0392b' },
  { tag: 'counterspell',label: '🛡 Counter',         color: '#8e44ad' },
  { tag: 'board_wipe',  label: '💣 Board Wipe',      color: '#e67e22' },
  { tag: 'tutor',       label: '🔍 Tutor',           color: '#16a085' },
  { tag: 'mana_rock',   label: '💎 Mana Rock',       color: '#7f8c8d' },
  { tag: 'mana_dork',   label: '🌿 Mana Dork',       color: '#1abc9c' },
  { tag: 'land_fetch',  label: '🗺 Land Fetch',       color: '#d35400' },
];

interface DeckCard {
  deckCardId:      number;
  cardId:          string;
  quantity:        number;
  isCommander:     boolean;
  isSideboard:     boolean;
  foil:            boolean;
  frameEffects:    string[];
  borderColor:     string;
  name:            string;
  setCode:         string;
  manaCost:        string;
  typeLine:        string;
  colors:          string;
  rarity:          string;
  reserved:        boolean;
  cmc:             number;
  tags:            string[];
  qtyOwned:        number;
  qtyOwnedRegular: number;
  qtyOwnedFoil:    number;
  qtyInAllDecks:   number;
  price:           number | null;
  imageSmall:      string;
  imageUrl:        string;
  overAllocated:   boolean;
  missing:         boolean;
}

interface Deck {
  deckId:             number;
  name:               string;
  format:             string | null;
  description:        string | null;
  playNotes:          string | null;
  status:             string;
  mainboardCards:     number;
  sideboardCards:     number;
  estimatedValue:     number;
  overAllocatedCount: number;
  missingCount:       number;
  cards:              DeckCard[];
}

interface AddPopup {
  card:        Card;
  isSideboard: boolean;
  foil:        boolean;
  x:           number;
  y:           number;
}

// ─── MANA CURVE CHART ─────────────────────────────────────────────────────────

function ManaCurve({ cards }: { cards: DeckCard[] }) {
  const mainboard = cards.filter(c => !c.isSideboard && !c.typeLine?.includes('Land'));
  if (mainboard.length === 0) return null;

  // Build CMC buckets 0-6+
  const buckets: Record<number, number> = { 0:0, 1:0, 2:0, 3:0, 4:0, 5:0, 6:0 };
  let totalCmc = 0, totalCards = 0;
  for (const card of mainboard) {
    const cmc = Math.min(Math.floor(card.cmc || 0), 6);
    buckets[cmc] = (buckets[cmc] || 0) + card.quantity;
    totalCmc   += (card.cmc || 0) * card.quantity;
    totalCards += card.quantity;
  }
  const avgCmc = totalCards > 0 ? (totalCmc / totalCards).toFixed(2) : '0.00';
  const maxCount = Math.max(...Object.values(buckets), 1);

  const W = 280, H = 80, barW = 30, gap = 8;
  const labels = ['0','1','2','3','4','5','6+'];

  return (
    <div className="mana-curve-wrap">
      <div className="mana-curve-title">
        Mana Curve <span className="mana-curve-avg">avg {avgCmc}</span>
      </div>
      <svg width={W} height={H + 20} style={{ overflow: 'visible' }}>
        {labels.map((label, i) => {
          const count  = buckets[i] || 0;
          const barH   = maxCount > 0 ? Math.round((count / maxCount) * H) : 0;
          const x      = i * (barW + gap);
          const y      = H - barH;
          const hue    = Math.round(200 - i * 22); // blue→red gradient
          const color  = `hsl(${hue},70%,55%)`;
          return (
            <g key={i}>
              <rect x={x} y={y} width={barW} height={barH}
                    fill={color} rx={3} opacity={0.85} />
              {count > 0 && (
                <text x={x + barW/2} y={y - 3} textAnchor="middle"
                      fontSize={9} fill="#a0a0b8">{count}</text>
              )}
              <text x={x + barW/2} y={H + 14} textAnchor="middle"
                    fontSize={9} fill="#505070">{label}</text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// ─── COLLAPSIBLE SECTION ──────────────────────────────────────────────────────

function DeckSection({
  title, count, defaultOpen, children,
}: {
  title: string; count: number; defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen ?? false);
  return (
    <div className="deck-section-wrap">
      <div className="deck-section-header" onClick={() => setOpen(o => !o)}>
        <span className="dsh-arrow">{open ? '▾' : '▸'}</span>
        <span className="dsh-title">{title}</span>
        <span className="dsh-count">{count}</span>
      </div>
      {open && <div className="deck-section-body">{children}</div>}
    </div>
  );
}

// ─── DECK CARD TILE ───────────────────────────────────────────────────────────

function DeckCardTile({
  card, onRemoveOne, onRightClick, onDragStart, width,
  onMouseEnter, onMouseLeave,
}: {
  card:          DeckCard;
  onRemoveOne:   (id: number) => void;
  onRightClick:  (card: DeckCard, e: React.MouseEvent) => void;
  onDragStart:   (card: DeckCard) => void;
  width:         number;
  onMouseEnter:  (url: string, e: React.MouseEvent) => void;
  onMouseLeave:  () => void;
}) {
  const stackOffset = Math.min(card.quantity - 1, 3) * 5;
  const h = Math.round(width * 1.4);

  return (
    <div className="deck-tile-wrap"
         style={{ width, height: h + stackOffset }}
         draggable
         onDragStart={() => onDragStart(card)}
         onClick={e => onRightClick(card, e)}
         onContextMenu={e => { e.preventDefault(); onRightClick(card, e); }}
         onMouseEnter={e => (card.imageUrl || card.imageSmall) && onMouseEnter(card.imageUrl || card.imageSmall, e)}
         onMouseLeave={onMouseLeave}
         title="Click to adjust quantity or remove">
      {/* Stack shadow layers */}
      {Array.from({ length: Math.min(card.quantity - 1, 3) }).map((_, i) => (
        <div key={i} className="tile-shadow"
             style={{ width, height: h, top: (Math.min(card.quantity-1,3)-i)*5,
                      borderRadius: width*0.05 }} />
      ))}
      {/* Top card */}
      <div className="tile-top" style={{ width, height: h }}>
        {card.imageSmall
          ? <img src={width > 110 ? (card.imageUrl || card.imageSmall) : card.imageSmall}
                 alt={card.name}
                 style={{ width:'100%', height:'100%',
                          borderRadius: width*0.05, display:'block' }} />
          : <div className="tile-placeholder">{card.name}</div>}
        {card.quantity > 1 && (
          <div className="tile-qty-badge"
               title={`${card.quantity} copies in deck`}>×{card.quantity}</div>
        )}
        {card.foil && (
          <div className="tile-foil-badge" title="Foil">✨</div>
        )}
        {card.overAllocated && (
          <div className="tile-badge-warn"
               title={`Over-allocated: ${card.qtyInAllDecks} assigned across decks, only own ${card.qtyOwned}`}>⚠</div>
        )}
        {card.missing && (
          <div className="tile-badge-missing" title="Not in your collection">!</div>
        )}
        {!card.missing && (
          <div className={`tile-owned ${card.overAllocated ? 'over' : 'ok'}`}
               title={card.overAllocated
                 ? `Own ${card.qtyOwned}, assigned ${card.qtyInAllDecks} across all decks`
                 : `You own ${card.qtyOwned}`}>
            {card.qtyOwned}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── BROWSER CARD TILE ────────────────────────────────────────────────────────

function BrowserCardTile({
  card, onClick, onContextMenu, onDragStart, width, inDeck, onMouseEnter, onMouseLeave,
}: {
  card:          Card;
  onClick:       (card: Card, e: React.MouseEvent) => void;
  onContextMenu?: (card: Card, e: React.MouseEvent) => void;
  onDragStart:   (card: Card) => void;
  width:         number;
  inDeck:        number;
  onMouseEnter:  (card: Card, e: React.MouseEvent) => void;
  onMouseLeave:  () => void;
}) {
  return (
    <div className={`browser-tile ${inDeck > 0 ? 'in-deck' : ''}`}
         style={{ width }}
         draggable
         onDragStart={() => onDragStart(card)}
         onClick={e => onClick(card, e)}
         onContextMenu={e => { if (onContextMenu) { e.preventDefault(); onContextMenu(card, e); } }}
         onMouseEnter={e => onMouseEnter(card, e)}
         onMouseLeave={onMouseLeave}>
      {card.imageSmall || card.imageUrl
        ? <img src={width >= 240 ? (card.imageLarge || card.imageUrl || card.imageSmall)
                   : width > 110  ? (card.imageUrl  || card.imageSmall)
                                  : card.imageSmall}
               alt={card.name}
               style={{ width:'100%', borderRadius: width*0.05, display:'block' }} />
        : <div className="browser-tile-placeholder"
               style={{ height: Math.round(width*1.4) }}>{card.name}</div>}
      {card.qtyOwned > 0 && (
        <div className="browser-owned-badge"
             title={card.isFoilOwned ? `You own ${card.qtyOwned} (foil)` : `You own ${card.qtyOwned}`}>
          ×{card.qtyOwned}
        </div>
      )}
      {card.isFoilOwned && (
        <div style={{
          position: 'absolute', top: 4, right: 4,
          background: 'rgba(200,164,90,0.85)',
          color: '#0a0c10',
          fontSize: 9, fontWeight: 700,
          padding: '2px 5px', borderRadius: 3,
          fontFamily: 'Outfit, sans-serif',
          pointerEvents: 'none', zIndex: 3,
          letterSpacing: '0.04em'
        }}>FOIL</div>
      )}
      {inDeck > 0 && (
        <div className="browser-indeck-badge"
             title={`${inDeck} cop${inDeck===1?'y':'ies'} in this deck`}>✓{inDeck}</div>
      )}
      <div className="browser-tile-info">
        <div className="browser-tile-name">{card.name}</div>
        <div className="browser-tile-meta">
          <span style={{ color: RARITY_COLOR[card.rarity] }}>
            {card.rarity?.charAt(0).toUpperCase()}
          </span>
          {card.price > 0 && <span className="browser-tile-price">${card.price.toFixed(2)}</span>}
        </div>
        {((card.frameEffects?.length ?? 0) > 0 || card.borderColor === 'borderless') && (
          <div className="browser-tile-variants">
            {card.borderColor === 'borderless' && (
              <span className="browser-variant-badge"
                    style={{ background: `${VARIANT_LABELS['borderless'].color}22`, color: VARIANT_LABELS['borderless'].color }}>
                Borderless
              </span>
            )}
            {card.frameEffects?.filter(fe => VARIANT_LABELS[fe] && fe !== 'borderless').map((fe, i) => (
              <span key={i} className="browser-variant-badge"
                    style={{ background: `${VARIANT_LABELS[fe].color}22`, color: VARIANT_LABELS[fe].color }}>
                {VARIANT_LABELS[fe].label}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── VARIANT BADGE LABELS ────────────────────────────────────────────────────
const VARIANT_LABELS: Record<string, { label: string; color: string }> = {
  borderless:    { label: 'Borderless',     color: '#4a90d9' },
  extendedart:   { label: 'Extended Art',   color: '#5dade2' },
  showcase:      { label: 'Showcase',       color: '#27ae60' },
  etched:        { label: 'Etched',         color: '#e67e22' },
  fullart:       { label: 'Full Art',       color: '#c8a45a' },
  inverted:      { label: 'Inverted',       color: '#b0b0cc' },
  colorshifted:  { label: 'Colorshifted',   color: '#9b59b6' },
  shatteredglass:{ label: 'Shattered Glass',color: '#a8c8e8' },
};

function VariantBadges({ frameEffects, borderColor }: {
  frameEffects: string[];
  borderColor?: string;
}) {
  const badges: { label: string; color: string }[] = [];
  if (borderColor === 'borderless') badges.push(VARIANT_LABELS['borderless']);
  if (frameEffects?.length) {
    frameEffects
      .filter(fe => VARIANT_LABELS[fe] && fe !== 'borderless')
      .forEach(fe => badges.push(VARIANT_LABELS[fe]));
  }
  if (!badges.length) return null;
  return (
    <>
      {badges.map((b, i) => (
        <span key={i} className="lr-variant-badge" style={{ background: `${b.color}22`, color: b.color, border: `1px solid ${b.color}44` }}>
          {b.label}
        </span>
      ))}
    </>
  );
}

// ─── RESIZABLE COLUMN WIDTHS ─────────────────────────────────────────────────

const DEFAULT_COL_WIDTHS = { qty: 32, name: 378, mana: 120, set: 56, owned: 60, price: 76, remove: 28 };

function ResizableHeader({
  cols, setCols
}: {
  cols: typeof DEFAULT_COL_WIDTHS;
  setCols: (c: typeof DEFAULT_COL_WIDTHS) => void;
}) {
  const dragging = React.useRef<{ col: keyof typeof DEFAULT_COL_WIDTHS; startX: number; startW: number } | null>(null);

  const onMouseDown = (col: keyof typeof DEFAULT_COL_WIDTHS) => (e: React.MouseEvent) => {
    e.preventDefault();
    dragging.current = { col, startX: e.clientX, startW: cols[col] };
    const onMove = (ev: MouseEvent) => {
      if (!dragging.current) return;
      const delta = ev.clientX - dragging.current.startX;
      const newW  = Math.max(30, dragging.current.startW + delta);
      setCols({ ...cols, [dragging.current.col]: newW });
    };
    const onUp = () => {
      dragging.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const th = (col: keyof typeof DEFAULT_COL_WIDTHS, label: string) => (
    <div className="lh-cell" style={{ width: cols[col], minWidth: cols[col] }}>
      {label}
      <div className="lh-resize-handle" onMouseDown={onMouseDown(col)} />
    </div>
  );

  const gridStyle = {
    gridTemplateColumns: `${cols.qty}px ${cols.name}px ${cols.mana}px ${cols.set}px ${cols.owned}px ${cols.price}px ${cols.remove}px`
  };
  return (
    <div className="list-header-row" style={gridStyle}>
      {th('qty',    'Qty')}
      {th('name',   'Card')}
      {th('mana',   'Cost')}
      {th('set',    'Set')}
      {th('owned',  'Owned')}
      {th('price',  'Price')}
      <div className="lh-cell" style={{ width: cols.remove }} />
    </div>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────

export default function DeckBuilderPage() {
  const { deckId } = useParams<{ deckId: string }>();
  const navigate   = useNavigate();
  const symbols    = useManaSymbols();

  // Deck state
  const [deck,         setDeck]       = useState<Deck | null>(null);
  const [deckLoading,  setDeckLoading]= useState(true);
  const { prefs } = useAuth();
  const [deckView,     setDeckView]   = useState<'cards'|'list'>(() => {
    const stored = localStorage.getItem('deckBuilder_view');
    if (stored === 'cards' || stored === 'list') return stored;
    return (prefs.defaultDeckView as 'cards'|'list') || 'cards';
  });
  const [deckGridSize, setDeckGridSize] = useState<GridSize>('sm');
  const [colWidths,    setColWidths]    = useState(DEFAULT_COL_WIDTHS);

  useEffect(() => { localStorage.setItem('deckBuilder_view', deckView); }, [deckView]);
  const [draggedDeckCard, setDraggedDeckCard] = useState<DeckCard|null>(null);
  const [targets,      setTargets]    = useState<Record<string, number>>({});
  const [editingTarget, setEditingTarget] = useState<string|null>(null);
  const [editTargetVal, setEditTargetVal] = useState(0);
  const [showStats,    setShowStats]  = useState(false);
  const [showImport,   setShowImport] = useState(false);
  const [contextMenu, setContextMenu] = useState<{
    x: number; y: number; items: ContextMenuItem[];
  } | null>(null);
  const [wishListModal, setWishListModal] = useState<{
    cardId: string; cardName: string; imageUrl?: string | null;
  } | null>(null);

  // Browser state
  const [query,        setQuery]      = useState('');
  const [cards,        setCards]      = useState<Card[]>([]);
  const [filters,      setFilters]    = useState<Filters>(DEFAULT_FILTERS);
  const [activeTags,   setActiveTags] = useState<string[]>([]);
  const [sets,         setSets]       = useState<SetOption[]>([]);
  const [page,         setPage]       = useState(0);
  const [total,        setTotal]      = useState(0);
  const [browsing,     setBrowsing]   = useState(false);
  const [gridSize,     setGridSize]   = useState<GridSize>('sm');
  const [filterPinned, setFilterPinned] = useState(false);
  const [showAllPrintings, setShowAllPrintings] = useState(false);
  const [draggedCard,  setDraggedCard]= useState<Card|null>(null);
  const [dropTarget,   setDropTarget] = useState<'mainboard'|'sideboard'|null>(null);
  const LIMIT = 48;

  // Popups
  const [addPopup,  setAddPopup]  = useState<AddPopup|null>(null);
  const [addQty,    setAddQty]    = useState(1);
  const [qtyPopup,  setQtyPopup]  = useState<{ card: DeckCard; x: number; y: number }|null>(null);
  const [qtyVal,    setQtyVal]    = useState(1);
  const [hoverCard, setHoverCard] = useState<{ url: string; x: number; y: number }|null>(null);
  const hoverTimer = useRef<ReturnType<typeof setTimeout>|null>(null);

  // ─── FETCH DECK ──────────────────────────────────────────────────────────────

  const fetchDeck = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/decks/${deckId}`);
      setDeck(res.data);
    } catch (e) { console.error(e); }
    finally { setDeckLoading(false); }
  }, [deckId]);

  const fetchTargets = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/decks/${deckId}/targets`);
      setTargets(res.data);
    } catch (e) { console.error(e); }
  }, [deckId]);

  const saveTarget = async (category: string, value: number) => {
    const updated = { ...targets, [category]: value };
    setTargets(updated);
    setEditingTarget(null);
    try {
      await axios.put(`${API}/decks/${deckId}/targets`, updated);
    } catch (e) { console.error(e); }
  };

  const applyPreset = async (format: string) => {
    try {
      const res = await axios.post(`${API}/decks/${deckId}/targets/preset`, { format });
      setTargets(res.data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { fetchDeck(); }, [fetchDeck]);

  // When deck loads, apply its format as default filter (only once)
  const VALID_LEGALITY_FORMATS = ['standard','pioneer','modern','legacy','vintage','commander','pauper','historic','explorer','alchemy','brawl','oathbreaker','oldschool','premodern','penny','duel'];
  const formatAppliedRef = useRef(false);
  useEffect(() => {
    if (formatAppliedRef.current) return;
    if (deck?.format) {
      const fmt = deck.format.toLowerCase();
      if (VALID_LEGALITY_FORMATS.includes(fmt)) {
        formatAppliedRef.current = true;
        setFilters(f => f.format ? f : { ...f, format: fmt });
      }
    }
  }, [deck?.format]);
  useEffect(() => { fetchTargets(); }, [fetchTargets]);
  useEffect(() => { axios.get(`${API}/sets`).then(r => setSets(r.data)).catch(console.error); }, []);

  // ─── FETCH CARDS ─────────────────────────────────────────────────────────────

  const abortRef = useRef<AbortController | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchCards = useCallback(async (pg = 0) => {
    // Cancel any in-flight request
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();
    const signal = abortRef.current.signal;

    setBrowsing(true);
    const parsed = parseQuery(query);
    try {
      const res = await axios.get(`${API}/cards/search`, {
        signal,
        params: {
          q:           parsed.name    || undefined,
          oracle:      parsed.oracle  || undefined,
          flavour:     parsed.flavour || undefined,
          tags:        activeTags.join(',') || undefined,
          limit:       LIMIT, page: pg,
          sortBy:      filters.sortBy,
          colors:      filters.colors.join(',')   || undefined,
          rarities:    filters.rarities.join(',') || undefined,
          typeLine:    filters.typeLine           || undefined,
          setCodes:    filters.setCodes.join(',') || undefined,
          format:      filters.format             || undefined,
          minPrice:    filters.minPrice           || undefined,
          maxPrice:    filters.maxPrice           || undefined,
          ownedOnly:   filters.ownedOnly          || undefined,
          showTokens:  filters.showTokens         || undefined,
          showDigital: filters.showDigital        || undefined,
          produces:    filters.produces?.join(',')|| undefined,
          showAllPrintings: showAllPrintings      || undefined,
          colorless:          filters.colorless         || undefined,
          producesColorless:  filters.producesColorless || undefined,
          cmcMin:    filters.cmcMin    || undefined,
          cmcMinOp:  filters.cmcMin    ? (filters.cmcMinOp || 'gte') : undefined,
          cmcMax:    filters.cmcMax    || undefined,
          cmcMaxOp:  filters.cmcMax    ? (filters.cmcMaxOp || 'lte') : undefined,
          pipsW:       filters.pipsW       || undefined,
          pipsU:       filters.pipsU       || undefined,
          pipsB:       filters.pipsB       || undefined,
          pipsR:       filters.pipsR       || undefined,
          pipsG:       filters.pipsG       || undefined,
          pipsC:       filters.pipsC       || undefined,
          pipsGeneric: filters.pipsGeneric || undefined,
          pipsX:       filters.pipsX       || undefined,
          pipMode:     filters.pipMode !== 'all'     ? filters.pipMode     : undefined,
          pipCount:    filters.pipCount !== 'atleast' ? filters.pipCount   : undefined,
          releaseYearFrom: filters.releaseYearFrom || undefined,
          releaseYearTo:   filters.releaseYearTo   || undefined,
        }
      });
      if (!signal.aborted) {
        setCards(res.data.cards);
        setTotal(res.data.total);
        setPage(pg);
      }
    } catch (e: any) {
      if (e?.code !== 'ERR_CANCELED') console.error(e);
    }
    finally { if (!signal.aborted) setBrowsing(false); }
  }, [query, filters, activeTags, showAllPrintings]);

  // Debounced fetch — 300ms delay to prevent rapid re-fires
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchCards(0), 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [fetchCards]);

  function parseQuery(q: string) {
    const lower = q.trim().toLowerCase();
    if (lower.startsWith('o:')) return { oracle: q.slice(2).trim() };
    if (lower.startsWith('f:')) return { flavour: q.slice(2).trim() };
    return { name: q.trim() };
  }

  // ─── TAG TOGGLE ──────────────────────────────────────────────────────────────

  const toggleTag = (tag: string) =>
    setActiveTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);

  // ─── DECK CARD MAP ────────────────────────────────────────────────────────────

  const deckCardMap = useMemo(() => {
    const map: Record<string, number> = {};
    if (!deck) return map;
    for (const c of deck.cards) {
      // Key by cardId only for browser tile "in deck" indicator
      map[c.cardId] = (map[c.cardId] || 0) + c.quantity;
    }
    return map;
  }, [deck]);

  // ─── ADD / REMOVE ─────────────────────────────────────────────────────────────

  const addCard = async (cardId: string, qty: number, isSideboard: boolean, foil: boolean = false) => {
    try {
      await axios.post(`${API}/decks/${deckId}/cards`, { cardId, quantity: qty, isSideboard, foil });
      fetchDeck();
    } catch (e) { console.error(e); }
  };

  const removeOneCopy = async (deckCardId: number) => {
    try {
      await axios.post(`${API}/decks/${deckId}/cards/${deckCardId}/remove-one`);
      fetchDeck();
    } catch (e) { console.error(e); }
  };

  const setQuantity = async (deckCardId: number, qty: number) => {
    if (qty < 1) {
      await axios.delete(`${API}/decks/${deckId}/cards/${deckCardId}`);
    } else {
      await axios.put(`${API}/decks/${deckId}/cards/${deckCardId}`, { quantity: qty });
    }
    fetchDeck();
    setQtyPopup(null);
  };

  // ─── DRAG HANDLERS ────────────────────────────────────────────────────────────

  const handleDropZoneDragOver = (e: React.DragEvent, zone: 'mainboard'|'sideboard') => {
    e.preventDefault(); setDropTarget(zone);
  };

  const handleDrop = (e: React.DragEvent, isSideboard: boolean) => {
    e.preventDefault(); setDropTarget(null);
    if (draggedCard) {
      setAddPopup({ card: draggedCard, isSideboard, foil: false, x: e.clientX, y: e.clientY });
      setAddQty(1); setDraggedCard(null);
    } else if (draggedDeckCard) {
      moveDeckCard(draggedDeckCard, isSideboard);
      setDraggedDeckCard(null);
    }
  };

  const moveDeckCard = async (card: DeckCard, isSideboard: boolean) => {
    if (card.isSideboard === isSideboard) return;
    await axios.delete(`${API}/decks/${deckId}/cards/${card.deckCardId}`);
    await axios.post(`${API}/decks/${deckId}/cards`, {
      cardId: card.cardId, quantity: card.quantity, isSideboard,
    });
    fetchDeck();
  };

  // ─── HOVER ────────────────────────────────────────────────────────────────────

  const showHover = (url: string, e: React.MouseEvent) => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    hoverTimer.current = setTimeout(() =>
      setHoverCard({ url, x: e.clientX, y: e.clientY }), 350);
  };
  const showHoverCard = (card: Card, e: React.MouseEvent) =>
    showHover(card.imageLarge || card.imageUrl || card.imageSmall || '', e);
  const hideHover = () => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    setHoverCard(null);
  };

  // ─── GROUP DECK CARDS ─────────────────────────────────────────────────────────

  const groupByType = (cards: DeckCard[]) => {
    const grouped: Record<string, DeckCard[]> = {};
    for (const card of cards) {
      const tl = (card.typeLine || '').split('—')[0]; // only check the main type, not subtype
      let type = 'Other';
      if (card.isCommander) {
        type = 'Commander';
      } else {
        // Check each type in order — skip 'Commander' since it's not a card type
        const matchOrder = ['Creature','Planeswalker','Battle','Instant','Sorcery','Enchantment','Artifact','Tribal','Land'];
        for (const t of matchOrder) {
          if (tl.includes(t)) { type = t; break; }
        }
      }
      if (!grouped[type]) grouped[type] = [];
      grouped[type].push(card);
    }
    for (const g of Object.values(grouped)) {
      g.sort((a, b) => (a.cmc || 0) - (b.cmc || 0) || a.name.localeCompare(b.name));
    }
    return grouped;
  };

  // ─── CATEGORY COUNTS ──────────────────────────────────────────────────────────

  const categoryCounts = useMemo(() => {
    if (!deck) return {} as Record<string, number>;
    const main = deck.cards.filter(c => !c.isSideboard);
    const qty  = (cards: DeckCard[]) => cards.reduce((s, c) => s + c.quantity, 0);
    const withTag = (tag: string) => main.filter(c => c.tags?.includes(tag));
    return {
      total:       qty(main),
      lands:       qty(main.filter(c => c.typeLine?.includes('Land'))),
      creatures:   qty(main.filter(c => c.typeLine?.split('—')[0].includes('Creature'))),
      ramp:        qty(withTag('ramp')),
      card_draw:   qty(withTag('card_draw')),
      removal:     qty(withTag('removal')),
      counterspell:qty(withTag('counterspell')),
      board_wipe:  qty(withTag('board_wipe')),
    };
  }, [deck]);

  const missingCost = useMemo(() => {
    if (!deck) return 0;
    return deck.cards
      .filter(c => c.missing && c.price != null)
      .reduce((s, c) => s + (c.price! * c.quantity), 0);
  }, [deck]);

  // ─── LIST VIEW ROW ────────────────────────────────────────────────────────────

  const listHeader = <ResizableHeader cols={colWidths} setCols={setColWidths} />;

  const renderListRow = (card: DeckCard) => {
    // ManaCost component handles DFC " // " separator natively
    const displayManaCost = card.manaCost || '';
    const gridStyle = {
      gridTemplateColumns: `${colWidths.qty}px ${colWidths.name}px ${colWidths.mana}px ${colWidths.set}px ${colWidths.owned}px ${colWidths.price}px ${colWidths.remove}px`
    };
    return (
      <div key={card.deckCardId}
           className={`list-row ${card.overAllocated?'over':''} ${card.missing?'miss':''}`}
           style={gridStyle}
           draggable onDragStart={() => setDraggedDeckCard(card)}
           onContextMenu={e => handleDeckCardContextMenu(card, e)}>
        <span className="lr-qty"
              onClick={e => { setQtyPopup({card, x:e.clientX, y:e.clientY}); setQtyVal(card.quantity); }}
              title="Click to adjust quantity or remove">
          {card.quantity}
        </span>
        <span className="lr-name"
              onMouseEnter={e => showHover(card.imageUrl || card.imageSmall || '', e)}
              onMouseLeave={hideHover}
              onClick={() => navigate(`/cards/${card.cardId}`, { state: { from:`/decks/${deckId}/build` }})}>
          {card.name}
          <VariantBadges frameEffects={card.frameEffects} borderColor={card.borderColor} />
          {card.foil && <span className="lr-foil-badge">Foil</span>}
        </span>
        <span className="lr-mana"><ManaCost manaCost={displayManaCost} size={12} /></span>
        <span className="lr-set">{card.setCode?.toUpperCase()}</span>
        <span className={`lr-owned ${card.missing?'miss':card.overAllocated?'over':'ok'}`}
              title={card.missing ? "Not in collection"
                   : card.overAllocated ? `Own ${card.qtyOwned}, assigned ${card.qtyInAllDecks} across decks`
                   : `Own ${card.qtyOwned}`}>
          {card.qtyOwned}
        </span>
        <span className="lr-price">{card.price ? `$${card.price.toFixed(2)}` : '—'}</span>
        <button className="lr-remove" title="Remove all copies"
                onClick={() => setQuantity(card.deckCardId, 0)}>✕</button>
      </div>
    );
  };

  // ─── RENDER DECK PANEL SECTIONS ───────────────────────────────────────────────

  const renderSection = (
    type: string, cards: DeckCard[], defaultOpen: boolean,
  ) => {
    const count = cards.reduce((s, c) => s + c.quantity, 0);
    const w = DECK_TILE_SIZE[deckGridSize];
    return (
      <DeckSection key={type} title={type} count={count} defaultOpen={defaultOpen}>
        {deckView === 'cards' ? (
          <div className="deck-tile-group">
            {cards.map(card => (
              <DeckCardTile key={card.deckCardId} card={card} width={w}
                onRemoveOne={removeOneCopy}
                onRightClick={(c, e) => handleDeckCardContextMenu(c, e)}
                onDragStart={setDraggedDeckCard}
                onMouseEnter={showHover}
                onMouseLeave={hideHover} />
            ))}
          </div>
        ) : (
          <div>{cards.map(renderListRow)}</div>
        )}
      </DeckSection>
    );
  };

  // ─── CONTEXT MENU HANDLERS ──────────────────────────────────────────────────

  const handleBrowserCardContextMenu = (card: Card, e: React.MouseEvent) => {
    e.preventDefault();
    setHoverCard(null);
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      items: [
        {
          icon: '⊕',
          label: 'Add to Mainboard',
          onClick: () => { setAddPopup({ card, isSideboard: false, foil: false, x: e.clientX, y: e.clientY }); setAddQty(1); },
        },
        {
          icon: '↕',
          label: 'Add to Sideboard',
          onClick: () => { setAddPopup({ card, isSideboard: true, foil: false, x: e.clientX, y: e.clientY }); setAddQty(1); },
        },
      ],
    });
  };

  const handleDeckCardContextMenu = (card: DeckCard, e: React.MouseEvent) => {
    e.preventDefault();
    setHoverCard(null);
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      items: [
        {
          icon: '🔢',
          label: 'Adjust Quantity',
          onClick: () => { setQtyPopup({ card, x: e.clientX, y: e.clientY }); setQtyVal(card.quantity); },
        },
        {
          icon: '🔍',
          label: 'View Card Detail',
          onClick: () => navigate(`/cards/${card.cardId}`, { state: { from: `/decks/${deckId}/build` } }),
        },
        {
          icon: '✕',
          label: 'Remove from Deck',
          onClick: () => setQuantity(card.deckCardId, 0),
          danger: true,
          divider: true,
        },
      ],
    });
  };

  if (deckLoading) return <div className="builder-loading">Loading deck…</div>;
  if (!deck)       return <div className="builder-loading">Deck not found.</div>;

  const mainboard   = deck.cards.filter(c => !c.isSideboard);
  const sideboard   = deck.cards.filter(c => c.isSideboard);
  const mainGrouped = groupByType(mainboard);
  const sideGrouped = groupByType(sideboard);
  return (
    <div className="deck-builder-page">

      {/* ── HOVER POPUP ── */}
      {hoverCard && hoverCard.url && !contextMenu && (
        <div style={{ position:'fixed', left: hoverCard.x+16,
                      top: Math.max(8, Math.min(hoverCard.y-100, window.innerHeight-420)),
                      zIndex:1500, pointerEvents:'none' }}>
          <img src={hoverCard.url} alt=""
               style={{ width:280, borderRadius:14, boxShadow:'0 12px 40px rgba(0,0,0,0.8)' }} />
        </div>
      )}

      {/* ── ADD QTY POPUP ── */}
      {addPopup && (
        <div className="popup-overlay" onClick={() => setAddPopup(null)}>
          <div className="add-popup"
               style={{ left: Math.min(addPopup.x, window.innerWidth-260),
                        top:  Math.min(addPopup.y+12, window.innerHeight-200) }}
               onClick={e => e.stopPropagation()}>
            <div className="ap-name">{addPopup.card.name}</div>
            <div className="ap-zone-row">
              <button className={`ap-zone ${!addPopup.isSideboard?'active':''}`}
                      onClick={() => setAddPopup({...addPopup, isSideboard:false})}>Mainboard</button>
              <button className={`ap-zone ${addPopup.isSideboard?'active':''}`}
                      onClick={() => setAddPopup({...addPopup, isSideboard:true})}>Sideboard</button>
              <button className={`ap-zone ${addPopup.foil?'active foil-btn':''}`}
                      onClick={() => setAddPopup({...addPopup, foil:!addPopup.foil})}
                      title="Toggle foil">
                {addPopup.foil ? 'Foil' : 'Regular'}
              </button>
            </div>
            <div className="ap-qty-row">
              {[1,2,3,4].map(n => (
                <button key={n} className={`ap-qty-btn ${addQty===n?'active':''}`}
                        onClick={() => setAddQty(n)}>{n}</button>
              ))}
              <input type="number" min={1} max={99} value={addQty}
                     className="ap-qty-input"
                     onChange={e => setAddQty(Number(e.target.value))} />
            </div>
            <button className="ap-confirm"
                    onClick={() => { addCard(addPopup.card.cardId, addQty, addPopup.isSideboard, addPopup.foil); setAddPopup(null); }}>
              Add {addQty} to {addPopup.isSideboard ? 'Sideboard' : 'Mainboard'}
            </button>
          </div>
        </div>
      )}

      {/* ── SET QTY POPUP ── */}
      {qtyPopup && (
        <div className="popup-overlay" onClick={() => setQtyPopup(null)}>
          <div className="qty-popup"
               style={{ left: Math.min(qtyPopup.x, window.innerWidth-240),
                        top:  Math.min(qtyPopup.y+12, window.innerHeight-200) }}
               onClick={e => e.stopPropagation()}>
            <div className="qp-name">{qtyPopup.card.name}</div>
            <div className="qp-label">Set quantity</div>
            <div className="ap-qty-row">
              {[1,2,3,4].map(n => (
                <button key={n} className={`ap-qty-btn ${qtyVal===n?'active':''}`}
                        onClick={() => setQtyVal(n)}>{n}</button>
              ))}
              <input type="number" min={1} max={99} value={qtyVal}
                     className="ap-qty-input"
                     onChange={e => setQtyVal(Number(e.target.value))} />
            </div>
            <button className="ap-confirm"
                    onClick={() => setQuantity(qtyPopup.card.deckCardId, qtyVal)}>
              Set to {qtyVal}
            </button>
            <div className="qp-divider" />
            <div className="qp-actions">
              <button className="qp-remove-one"
                      onClick={() => { removeOneCopy(qtyPopup.card.deckCardId); setQtyPopup(null); }}>
                − Remove One Copy
              </button>
              <button className="qp-remove-all"
                      onClick={() => setQuantity(qtyPopup.card.deckCardId, 0)}>
                ✕ Remove All Copies
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══ LEFT: CARD BROWSER ══ */}
      <div className={`builder-left ${filterPinned?'filter-pinned':''}`}>
        <div className="builder-left-top">
          <div className="builder-top-row">
            <button className="builder-back" onClick={() => navigate(-1)}>
              ← {deck.name}
            </button>
            <div className="builder-grid-btns">
              {GRID_SIZES.map(s => (
                <button key={s} className={`grid-btn ${gridSize===s?'active':''}`}
                        onClick={() => setGridSize(s)}>{s.toUpperCase()}</button>
              ))}
              <button
                className={`grid-btn ${showAllPrintings && !filters.ownedOnly ? 'active' : ''} ${filters.ownedOnly ? 'disabled' : ''}`}
                onClick={() => { if (!filters.ownedOnly) setShowAllPrintings(v => !v); }}
                title={filters.ownedOnly ? 'Owned Only shows all your printings automatically — disable Owned Only to use this filter' : showAllPrintings ? 'Showing all printings' : 'Showing one per card'}
                style={filters.ownedOnly ? { opacity: 0.35, cursor: 'not-allowed' } : {}}>
                ⊞
              </button>
            </div>
          </div>
          <SearchBar value={query} onChange={v => setQuery(v)} />
          {/* Tag quick filters */}
          <div className="tag-filter-row">
            {TAG_CONFIG.map(t => (
              <button key={t.tag}
                      className={`tag-pill ${activeTags.includes(t.tag)?'active':''}`}
                      style={{ '--tag-color': t.color } as React.CSSProperties}
                      onClick={() => toggleTag(t.tag)}
                      title={t.tag.replace(/_/g,' ')}>
                {t.label}
              </button>
            ))}
            {activeTags.length > 0 && (
              <button className="tag-clear" onClick={() => setActiveTags([])}>✕ Clear</button>
            )}
          </div>
          <FilterDrawer filters={filters} sets={sets}
                        onChange={f => setFilters(f)}
                        onPinChange={p => setFilterPinned(p)} />
        </div>

        <div className="builder-card-count">
          {browsing ? 'Searching…' : `${total.toLocaleString()} cards`}
          {activeTags.length > 0 && (
            <span className="active-tag-list"> · {activeTags.map(t => t.replace(/_/g,' ')).join(', ')}</span>
          )}
        </div>

        {cards.length === 0 && !browsing && (
          <div className="builder-empty-state">
            {query || filters.format || activeTags.length > 0
              ? <span>No cards found — try adjusting your search or filters</span>
              : <span>🔍 Search for cards above to browse your collection</span>}
          </div>
        )}
        <div className="builder-card-grid" onDragOver={e => e.preventDefault()}>
          {cards.map(card => (
            <BrowserCardTile key={`${card.cardId}-${card.isFoilOwned ? 'foil' : 'reg'}`} card={card}
              onClick={(c,e) => { setAddPopup({card:c, isSideboard:false, foil:false, x:e.clientX, y:e.clientY}); setAddQty(1); }}
              onContextMenu={(c,e) => handleBrowserCardContextMenu(c, e)}
              onDragStart={c => setDraggedCard(c)}
              width={GRID_WIDTHS[gridSize]}
              inDeck={deckCardMap[card.cardId] || 0}
              onMouseEnter={showHoverCard}
              onMouseLeave={hideHover} />
          ))}
        </div>

        {total > LIMIT && (
          <div className="builder-pagination">
            <button disabled={page===0} onClick={() => fetchCards(page-1)}>← Prev</button>
            <span>{page+1} / {Math.ceil(total/LIMIT)}</span>
            <button disabled={(page+1)*LIMIT >= total} onClick={() => fetchCards(page+1)}>Next →</button>
          </div>
        )}
      </div>

      {/* ══ RIGHT: DECK PANEL ══ */}
      <div className="builder-right">

        {/* Deck header — compact, always visible */}
        <div className="builder-deck-header">
          <div className="bdh-title-row">
            <span className="bdh-name">{deck.name}</span>
            {deck.format && <span className="bdh-format">{deck.format}</span>}
            <div className="bdh-right-controls">
              <button className={`view-btn ${deckView==='cards'?'active':''}`}
                      onClick={() => setDeckView('cards')} title="Card view">⊞</button>
              <button className={`view-btn ${deckView==='list'?'active':''}`}
                      onClick={() => setDeckView('list')} title="List view">≡</button>
              {deckView==='cards' && (['sm','md','lg'] as GridSize[]).map(s => (
                <button key={s} className={`grid-btn ${deckGridSize===s?'active':''}`}
                        onClick={() => setDeckGridSize(s)}>{s.toUpperCase()}</button>
              ))}
              <button className="import-btn"
                      onClick={() => setShowImport(true)}
                      title="Import card list">
                ↑ Import
              </button>
              <button className={`stats-toggle-btn ${showStats?'active':''}`}
                      onClick={() => setShowStats(v => !v)}
                      title="Toggle mana curve and targets">
                {showStats ? '▲ Stats' : '▼ Stats'}
              </button>
            </div>
          </div>

          {/* Stats pills — always visible, single line */}
          <div className="bdh-stats">
            <span className="bdh-pill" title="Cards in mainboard">{deck.mainboardCards} main</span>
            {deck.sideboardCards > 0 &&
              <span className="bdh-pill" title="Cards in sideboard">{deck.sideboardCards} side</span>}
            <span className="bdh-pill" title="Estimated deck value">${deck.estimatedValue.toFixed(0)}</span>
            {deck.missingCount > 0 &&
              <span className="bdh-pill missing"
                    title={`${deck.missingCount} card(s) not owned — est. $${missingCost.toFixed(0)} to acquire`}>
                {deck.missingCount} missing · ${missingCost.toFixed(0)}
              </span>}
            {deck.overAllocatedCount > 0 &&
              <span className="bdh-pill warn"
                    title="Cards assigned to more decks than copies owned">
                ⚠ {deck.overAllocatedCount} over
              </span>}
          </div>

          {/* Collapsible stats section */}
          {showStats && (
            <div className="bdh-stats-expanded">
              <ManaCurve cards={deck.cards} />
              {Object.keys(targets).length > 0 && (
                <div className="target-bars">
                  {CATEGORIES.map(cat => (
                    targets[cat.key] > 0 && (
                      editingTarget === cat.key ? (
                        <div key={cat.key} className="target-bar-row">
                          <div className="tbr-label">{cat.label}</div>
                          <input type="number" min={0} max={200}
                                 className="tbr-edit-input"
                                 value={editTargetVal}
                                 autoFocus
                                 onChange={e => setEditTargetVal(Number(e.target.value))}
                                 onBlur={() => saveTarget(cat.key, editTargetVal)}
                                 onKeyDown={e => {
                                   if (e.key === 'Enter')  saveTarget(cat.key, editTargetVal);
                                   if (e.key === 'Escape') setEditingTarget(null);
                                 }} />
                        </div>
                      ) : (
                        <TargetBar key={cat.key}
                          label={cat.label}
                          current={categoryCounts[cat.key] || 0}
                          target={targets[cat.key]}
                          onEditTarget={() => {
                            setEditingTarget(cat.key);
                            setEditTargetVal(targets[cat.key]);
                          }} />
                      )
                    )
                  ))}
                </div>
              )}
              <div className="target-preset-row">
                {Object.keys(targets).length === 0 && deck.format && (
                  <button className="target-preset-btn"
                          onClick={() => applyPreset(deck.format!)}>
                    Apply {deck.format} targets
                  </button>
                )}
                {Object.keys(targets).length === 0 && !deck.format && (
                  <div className="target-hint">Set a deck format to auto-populate targets</div>
                )}
                {Object.keys(targets).length > 0 && (
                  <button className="target-reset-btn"
                          onClick={() => deck.format && applyPreset(deck.format)}>
                    ↺ Reset to {deck.format || 'default'} preset
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Drop zones */}
        <div className="builder-deck-body">

          {/* MAINBOARD */}
          <div className={`drop-zone ${dropTarget==='mainboard'?'drag-over':''}`}
               onDragOver={e => handleDropZoneDragOver(e,'mainboard')}
               onDragLeave={() => setDropTarget(null)}
               onDrop={e => handleDrop(e, false)}>
            <div className="zone-label">Mainboard · {deck.mainboardCards}</div>
            {mainboard.length === 0
              ? <div className="zone-empty">Drop cards here to build your deck</div>
              : <>
                  {deckView === 'list' && listHeader}
                  {TYPE_ORDER
                    .filter(t => mainGrouped[t])
                    .map((t, i) => renderSection(t, mainGrouped[t], i === 0))}
                </>}
          </div>

          {/* SIDEBOARD */}
          <div className={`drop-zone sideboard ${dropTarget==='sideboard'?'drag-over':''}`}
               onDragOver={e => handleDropZoneDragOver(e,'sideboard')}
               onDragLeave={() => setDropTarget(null)}
               onDrop={e => handleDrop(e, true)}>
            <div className="zone-label">Sideboard · {deck.sideboardCards}</div>
            {sideboard.length === 0
              ? <div className="zone-empty">Drop sideboard cards here</div>
              : TYPE_ORDER
                  .filter(t => sideGrouped[t])
                  .map((t, i) => renderSection(t, sideGrouped[t], i === 0))}
          </div>
        </div>
      </div>

      {/* ── CONTEXT MENU ── */}
      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          items={contextMenu.items}
          onClose={() => setContextMenu(null)}
        />
      )}
      {wishListModal && (
        <AddToWishListModal
          cardId={wishListModal.cardId}
          cardName={wishListModal.cardName}
          imageUrl={wishListModal.imageUrl}
          onDone={() => setWishListModal(null)}
          onCancel={() => setWishListModal(null)}
        />
      )}

      {/* ── IMPORT MODAL ── */}
      {showImport && deck && (
        <DeckImport
          deckId={deck.deckId}
          deckName={deck.name}
          onDone={() => fetchDeck()}
          onClose={() => setShowImport(false)}
        />
      )}
    </div>
  );
}
